/**
 * ToastContext
 * Global context for managing toast notifications
 */

import React, { createContext, useContext, useState, useCallback } from "react";
import { Toast, ToastProps } from "../components/Toast";

interface ToastContextType {
    showToast: (toast: Omit<ToastProps, "id" | "onClose">) => void;
    hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};

interface ToastProviderProps {
    children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);

    const showToast = useCallback(
        (toast: Omit<ToastProps, "id" | "onClose">) => {
            const id = `toast-${Date.now()}-${Math.random()}`;
            setToasts((prev) => [...prev, { ...toast, id }]);
        },
        []
    );

    const hideToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, hideToast }}>
            {children}
            <div
                style={{
                    position: "fixed",
                    bottom: "20px",
                    right: "20px",
                    zIndex: 10000,
                    display: "flex",
                    flexDirection: "column-reverse",
                    gap: "12px",
                    pointerEvents: "none",
                }}
            >
                {toasts.map((toast) => (
                    <div
                        key={toast.id}
                        style={{
                            pointerEvents: "auto",
                            animation: "slideIn 0.3s ease-out",
                        }}
                    >
                        <Toast
                            {...toast}
                            onClose={() => hideToast(toast.id!)}
                        />
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
